import PropTypes from "prop-types";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useSnackbar } from "notistack";
// @mui
import {
  Box,
  Stack,
  Button,
  Divider,
  Typography,
  CircularProgress,
  InputAdornment,
  Autocomplete,
  TextField,
  Dialog,
  DialogTitle,
  IconButton,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tooltip,
  Avatar,
  ButtonGroup,
  Popper,
  MenuItem,
  Grow,
  Paper,
  ClickAwayListener,
  MenuList,
} from "@mui/material";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import { LoadingButton } from "@mui/lab";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Icon } from "@iconify/react";
import eyeFill from "@iconify/icons-eva/eye-fill";
import eyeOffFill from "@iconify/icons-eva/eye-off-fill";
// utils
import { ASSETS_PATH, CheckPerm, getFirstCharacter } from "../../../utils/apis";
import { fNumber, isNumeric } from "../../../utils/formatNumber";
import useAuth from "../../../hooks/useAuth";
import { getOfflineData } from "../../../utils/jwt";

RequestAccess.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  handleGrant: PropTypes.func,
  setValue: PropTypes.func,
  handleShowPassword: PropTypes.func,
  values: PropTypes.object,
  loading: PropTypes.bool,
  showPassword: PropTypes.bool,
};
function RequestAccess({
  open,
  handleClose,
  handleGrant,
  values,
  setValue,
  showPassword,
  handleShowPassword,
  loading,
}) {
  return (
    <Dialog sx={{ textAlign: "center" }} open={open} onClose={handleClose}>
      <DialogTitle>Grant Permission to update price.</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 5 }}>
          Please enter your email address and password to proceed.
        </DialogContentText>
        <Stack spacing={{ xs: 2, md: 3 }}>
          <TextField
            type="text"
            value={values.username}
            autoComplete="username"
            name="username"
            fullWidth
            label="Username / Email address"
            onChange={setValue}
          />
          <TextField
            fullWidth
            value={values.password}
            autoComplete="current-password"
            type={showPassword ? "text" : "password"}
            name="password"
            label="Password"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={handleShowPassword} edge="end">
                    <Icon icon={showPassword ? eyeFill : eyeOffFill} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            onChange={setValue}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ mx: "auto" }}>
        <Button onClick={handleClose} color="inherit" sx={{ mr: 5 }}>
          Cancel
        </Button>
        <LoadingButton
          loading={loading}
          onClick={handleGrant}
          color="warning"
          variant="contained"
        >
          Grant Access
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
}

InvoiceNewEditDetails.propTypes = {
  formik: PropTypes.object,
  isEdit: PropTypes.bool,
  isOnline: PropTypes.bool,
  branch: PropTypes.object,
  payable: PropTypes.number,
};
export default function InvoiceNewEditDetails({
  formik,
  branch,
  payable,
  isOnline,
}) {
  const { values, setFieldValue } = formik;
  const { getProducts, user, checkPriceChangePerm } = useAuth();
  const { settings } = user;
  const canEditPrice = parseInt(
    settings.filter((setting) => setting.name === "allow_edit_price")[0].value,
    10,
  );
  const allowOutOfStock =
    parseInt(
      settings.filter((setting) => setting.name === "out_of_stock_sales")[0]
        .value,
      10,
    ) === 1;

  const hasEditPricePerm = CheckPerm("allow_product_price_change");
  const [currentIndex, setCurrentIndex] = useState(-1);
  const [search, setSearch] = useState("");
  const [options, setOptions] = useState([]);
  const [open, setOpen] = useState(false);
  const [allowEdit, setAllowEdit] = useState(hasEditPricePerm);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openQty, setOpenQty] = useState(null);
  const [qtySelectedIndex, setQtySelectedIndex] = useState(0);
  const [loginValues, setLoginValues] = useState({
    username: "",
    password: "",
  });
  const qtyRef = useRef();

  const branchId = branch && branch.bid ? branch.bid : "";
  const { enqueueSnackbar } = useSnackbar();

  const allOfflineProducts = useMemo(() => {
    try {
      return getOfflineData("products") || [];
    } catch (e) {
      console.error("Failed to parse offline data", e);
      return [];
    }
  }, []);

  const handleSelect = useCallback(
    (product, index) => {
      if (product) {
        setFieldValue(`products[${index}]`, {
          pid: product.pid,
          name: product.name,
          quantity:
            parseInt(product.qty, 10) < 1 && !allowOutOfStock ? "0" : "1",
          displayedQuantity:
            parseInt(product.qty, 10) < 1 && !allowOutOfStock ? "0" : "1",
          qty: parseInt(product.qty, 10),
          price: parseFloat(product.selling_price),
          cost: parseFloat(product.cost_price),
          sell: parseFloat(product.selling_price),
          wholesale: parseFloat(product.wholesalePrice),
          vat: parseFloat(product.vat),
          selectedType: {
            name: "Single",
            quantity: 1,
            price: parseFloat(product.selling_price),
          },
          types: product.qtyPrices ? JSON.parse(product.qtyPrices) : [],
          isService: product.isService,
          milestones:
            parseInt(product.isService, 10) === 1
              ? JSON.parse(product.attributes)
              : [],
        });
        setOptions([]);
        if (
          parseInt(product.isService, 10) === 0 &&
          parseInt(product.qty, 10) <= 0
        ) {
          enqueueSnackbar(`${product.name}'s stock is empty! Please top up.`, {
            variant: "warning",
          });
        }
      }
    },
    [allowOutOfStock, setFieldValue, enqueueSnackbar],
  );

  useEffect(() => {
    // 1. Create a controller for this specific request
    const controller = new AbortController();
    const { signal } = controller;

    // 2. Setup Debounce (e.g., 500ms delay)
    const timer = setTimeout(async () => {
      if (currentIndex >= 0 && search.trim() !== "") {
        try {
          setFieldValue(`products[${currentIndex}].loading`, true);

          // Pass the 'signal' to your API helper
          const data = await getProducts(
            search,
            branchId,
            false,
            isOnline,
            {
              signal,
            },
            allOfflineProducts,
          );

          setOptions(data);
          setFieldValue(`products[${currentIndex}].loading`, false);

          if (data.length === 1 && data[0].number === search) {
            handleSelect(data[0], currentIndex);
          }
        } catch (error) {
          // Only log errors that aren't caused by a deliberate abort
          if (error.name !== "AbortError") {
            console.error("Search failed:", error);
            setFieldValue(`products[${currentIndex}].loading`, false);
          }
        }
      }
    }, 500);

    // 3. Cleanup: This runs every time 'search' changes or the component unmounts
    return () => {
      clearTimeout(timer); // Cancel the pending timer
      controller.abort(); // Cancel the pending API request
    };
  }, [
    search,
    branchId,
    isOnline,
    currentIndex,
    getProducts,
    setFieldValue,
    allOfflineProducts,
    handleSelect,
  ]);

  const setAmount = useCallback(() => {
    setFieldValue(`amount`, payable);
  }, [payable, setFieldValue]);

  useEffect(() => {
    setAmount();
  }, [setAmount]);

  const handleAdd = () => {
    const newProducts = [
      ...values.products,
      {
        pid: "",
        name: "",
        warranty: "",
        quantity: "",
        displayedQuantity: "",
        types: [],
        selectedType: {},
        cost: "",
        sell: "",
        price: "",
        total: "",
        editPrice: false,
        isService: "0",
        qty: 0,
        loading: false,
        open: false,
      },
    ];
    setFieldValue("products", newProducts);
  };
  const handleAddQty = (toAdd, index, isReplace = false) => {
    try {
      const item = values.products[index];
      const itemQty = parseFloat(
        (item.selectedType && item.selectedType.quantity) || 1,
      );
      const qty = Number(values.products[index].displayedQuantity || "0");
      const prodQty = Number(values.products[index].qty || 0);
      const isProduct = parseInt(values.products[index].isService, 10) === 0;

      const safeToAdd = isNumeric(toAdd) ? Math.max(0, parseFloat(toAdd)) : 0;
      const candidateDispQty = isReplace
        ? safeToAdd
        : Math.max(0, qty + safeToAdd);
      const newQty = candidateDispQty * itemQty;

      if (isProduct && newQty > prodQty) {
        const productName = values.products[index].name;
        if (!allowOutOfStock) {
          const maxAllowedDispQty = Math.max(0, Math.floor(prodQty / itemQty));
          const clampedActualQty = maxAllowedDispQty * itemQty;
          setFieldValue(
            `products[${index}].displayedQuantity`,
            maxAllowedDispQty,
          );
          setFieldValue(`products[${index}].quantity`, clampedActualQty);
          enqueueSnackbar(
            `${productName}'s quantity (${prodQty}) is too low for the requested quantity. Clamped to available stock (${maxAllowedDispQty}).`,
            { variant: "warning" },
          );
          return;
        }
        enqueueSnackbar(
          `${productName}'s quantity (${prodQty}) is too low for the requested quantity.`,
          { variant: "warning" },
        );
      }
      setFieldValue(`products[${index}].displayedQuantity`, candidateDispQty);
      setFieldValue(`products[${index}].quantity`, newQty);
    } catch (err) {
      // console.log(err);
    }
  };

  const handleRemove = (index) => {
    if (index > -1) {
      const newProducts = values.products.filter((_, i) => i !== index);
      setFieldValue("products", newProducts);
      setCurrentIndex(-1);
    }
  };

  const handleSearch = (search, index) => {
    setCurrentIndex(index);
    setSearch(search);
  };
  const handlePriceEdit = (index) => {
    if (!allowEdit) {
      setOpen(true);
      return;
    }
    const isEdit = values.products[index].editPrice;
    if (isEdit) {
      // revert back
      const type = values.products[index].selectedType || {};
      const types = values.products[index].types || [];
      const price =
        (type &&
          type.name &&
          types.length > 0 &&
          types.filter((t) => t.name === type.name).length > 0 &&
          types.filter((t) => t.name === type.name)[0].price) ||
        values.products[index].sell;
      setFieldValue(`products[${index}].price`, price);
    }
    setFieldValue(`products[${index}].editPrice`, !isEdit);
    setFieldValue("priceChange", user.user.uid);
  };
  // const handleRetailWholesaleSwitch = (index) => {
  //   const sell = parseFloat(values.products[index].sell);
  //   const wholesale = parseFloat(values.products[index].wholesale);
  //   if (sell === parseFloat(values.products[index].price)) {
  //     // revert back
  //     setFieldValue(`products[${index}].price`, wholesale);
  //   } else {
  //     setFieldValue(`products[${index}].price`, sell);
  //   }
  // };
  const handleGrant = async () => {
    try {
      const { username, password } = loginValues;
      if (username && password) {
        setLoading(true);
        const admin = await checkPriceChangePerm(loginValues);
        setLoginValues({ username: "", password: "" });
        setAllowEdit(true);
        setOpen(false);
        setFieldValue("priceChange", admin);
        enqueueSnackbar(`Access Granted! You can now edit prices.`, {
          variant: "success",
        });
      } else {
        enqueueSnackbar(
          `Please enter a username or email address and password to proceed.`,
          { variant: "error" },
        );
      }
    } catch (err) {
      enqueueSnackbar(err.message, { variant: "error" });
    }
    setLoading(false);
  };
  const handleValuesChange = (e) => {
    const { name, value } = e.target;
    setLoginValues((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };
  const handleQtyToggle = (target, index) => {
    setOpenQty(openQty ? null : target);
    setQtySelectedIndex(index);
  };
  const handleQtyClose = () => {
    setOpenQty(null);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ color: "text.disabled", mb: 3 }}>
        Products:
      </Typography>
      <Stack
        divider={<Divider flexItem sx={{ borderStyle: "dashed" }} />}
        spacing={3}
      >
        {values.products.map((item, index) => (
          <Stack
            key={index}
            direction={{ xs: "column", md: "row" }}
            spacing={2}
            sx={{ width: 1 }}
          >
            <Stack>
              <Autocomplete
                sx={{ maxWidth: { md: 300 }, width: 300 }}
                size="small"
                value={item}
                getOptionLabel={(option) =>
                  typeof option === "string" ? option : option.name
                }
                options={options}
                onChange={(event, newValue) => {
                  handleSelect(newValue, index);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // ONLY search if the change comes from manual typing
                  if (reason === "input") {
                    setFieldValue(`products[${index}].name`, newInputValue);
                    handleSearch(newInputValue, index);
                  }
                }}
                isOptionEqualToValue={(option, value) =>
                  parseInt(option.pid, 10) === parseInt(value.pid, 10)
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Product"
                    variant="outlined"
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {values.products[index].loading ? (
                            <CircularProgress color="inherit" size={20} />
                          ) : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <li {...props}>
                    <Box
                      sx={{
                        py: 1,
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Avatar
                        src={
                          option.featuredPhoto
                            ? `${ASSETS_PATH.products}${option.featuredPhoto}`
                            : ""
                        }
                        sx={{ mr: 1, bgcolor: "background.neutral" }}
                      >
                        {getFirstCharacter(option.name)}
                      </Avatar>
                      <Typography variant="body2">{option.name}</Typography>
                    </Box>
                  </li>
                )}
              />
              {item.types && item.types.length > 0 && (
                <Autocomplete
                  size="small"
                  value={item.selectedType || {}}
                  getOptionLabel={(option) =>
                    typeof option === "string" ? option : `${option.name}`
                  }
                  sx={{ py: 2 }}
                  options={[
                    {
                      name: "Single",
                      quantity: 1,
                      price: parseFloat(item.sell),
                    },
                    ...item.types,
                  ]}
                  onChange={(event, newValue) => {
                    if (newValue) {
                      const typeMultiplier = parseInt(
                        newValue.quantity || 1,
                        10,
                      );
                      const currentDispQty = parseFloat(
                        item.displayedQuantity || 1,
                      );
                      const potentialQty = typeMultiplier * currentDispQty;
                      const isProduct = parseInt(item.isService, 10) === 0;
                      const prodQty = Number(item.qty || 0);

                      if (
                        isProduct &&
                        !allowOutOfStock &&
                        potentialQty > prodQty
                      ) {
                        const maxPacks = Math.floor(prodQty / typeMultiplier);
                        if (maxPacks < 1) {
                          enqueueSnackbar(
                            `Cannot select ${newValue.name}: requires at least ${typeMultiplier} units, but only ${prodQty} available in stock.`,
                            { variant: "warning" },
                          );
                          return;
                        }
                        enqueueSnackbar(
                          `Adjusted quantity to ${maxPacks} ${newValue.name}(s) to match available stock (${prodQty}).`,
                          { variant: "warning" },
                        );
                        setFieldValue(
                          `products[${index}].selectedType`,
                          newValue,
                        );
                        setFieldValue(
                          `products[${index}].displayedQuantity`,
                          maxPacks,
                        );
                        setFieldValue(
                          `products[${index}].quantity`,
                          maxPacks * typeMultiplier,
                        );
                        setFieldValue(
                          `products[${index}].price`,
                          parseFloat(newValue.price || 0),
                        );
                        return;
                      }

                      setFieldValue(
                        `products[${index}].selectedType`,
                        newValue,
                      );
                      setFieldValue(
                        `products[${index}].quantity`,
                        typeMultiplier * currentDispQty,
                      );
                      setFieldValue(
                        `products[${index}].price`,
                        parseFloat(newValue.price || 0),
                      );
                    } else {
                      setFieldValue(`products[${index}].selectedType`, {
                        name: "Single",
                        quantity: 1,
                        price: parseFloat(item.sell),
                      });
                      setFieldValue(
                        `products[${index}].price`,
                        parseFloat(item.sell),
                      );
                      setFieldValue(
                        `products[${index}].quantity`,
                        1 * parseFloat(item.displayedQuantity || 1),
                      );
                    }
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option.name === value.name
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Type"
                      variant="outlined"
                      InputProps={{
                        ...params.InputProps,
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props}>
                      <Box
                        sx={{
                          py: 1,
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <Typography variant="body2">
                          {option.name} (Qty: {option.quantity})
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              )}
              {values.products[index].name &&
                parseInt(values.products[index].isService, 10) === 0 && (
                  <Typography
                    variant="caption"
                    sx={{ pt: 1, color: "text.secondary" }}
                  >
                    Available Quantity: {fNumber(values.products[index].qty)}
                  </Typography>
                )}
            </Stack>

            <Stack spacing={1}>
              <TextField
                size="small"
                type="number"
                sx={{ maxWidth: { md: 200 }, width: "100%" }}
                value={values.products[index].displayedQuantity}
                name={`products.${index}.displayedQuantity`}
                label="Qty"
                onChange={(event) => {
                  const displayedQuantity = isNumeric(event.target.value)
                    ? parseFloat(event.target.value)
                    : 0;
                  handleAddQty(displayedQuantity, index, true);
                }}
              />
              {values.products[index].name &&
                parseInt(values.products[index].isService, 10) === 0 &&
                values.products[index].selectedType &&
                parseInt(
                  values.products[index].selectedType.quantity || 1,
                  10,
                ) > 1 && (
                  <Typography
                    variant="caption"
                    sx={{ pt: 1, color: "text.secondary" }}
                  >
                    Actual Qty Sold: {fNumber(values.products[index].quantity)}
                  </Typography>
                )}
              <ButtonGroup variant="contained" ref={qtyRef}>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => {
                    handleAddQty(1, index);
                  }}
                >
                  &nbsp;&nbsp;&nbsp;+1&nbsp;&nbsp;&nbsp;
                </Button>
                <Button
                  size="small"
                  aria-controls={openQty ? "split-button-menu" : undefined}
                  aria-expanded={openQty ? "true" : undefined}
                  aria-label="select merge strategy"
                  aria-haspopup="menu"
                  onClick={(e) => handleQtyToggle(e.currentTarget, index)}
                >
                  <ArrowDropDownIcon />
                </Button>
              </ButtonGroup>
            </Stack>

            <Stack direction="column" alignItems="flex-start" spacing={0}>
              <TextField
                size="small"
                type="number"
                sx={{
                  width: { md: "auto", xs: "100%" },
                }}
                disabled={!values.products[index].editPrice}
                name={`products.${index}.price`}
                value={values.products[index].price}
                label="Unit Price"
                onChange={(event) =>
                  setFieldValue(
                    `products[${index}].price`,
                    Number(event.target.value),
                  )
                }
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">&#8358;</InputAdornment>
                  ),
                }}
              />
              {canEditPrice === 1 && values.products[index].price !== "" && (
                <Button
                  sx={{ justifyContent: "flex-start" }}
                  size="small"
                  color="warning"
                  onClick={() => handlePriceEdit(index)}
                >
                  {(values.products[index].editPrice && "Revert") || "Edit"}
                </Button>
              )}
            </Stack>

            <TextField
              disabled
              size="small"
              name={`products.${index}.total`}
              label="Total"
              value={fNumber(
                values.products[index].displayedQuantity *
                  values.products[index].price,
              )}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">&#8358;</InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              type="text"
              value={values.products[index].warranty}
              name={`products.${index}.warranty`}
              label="Warranty"
              onChange={(event) =>
                setFieldValue(`products[${index}].warranty`, event.target.value)
              }
            />
            <Button
              size="small"
              color="error"
              sx={{ alignSelf: "flex-start" }}
              startIcon={<DeleteIcon />}
              onClick={() => handleRemove(index)}
            >
              Remove
            </Button>
          </Stack>
        ))}
      </Stack>

      <Divider sx={{ my: 6, borderStyle: "dashed" }} />

      <Stack
        spacing={2}
        direction={{ xs: "column-reverse", md: "row" }}
        alignItems={{ xs: "flex-start", md: "start" }}
      >
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          sx={{ flexShrink: 0 }}
        >
          Add product
        </Button>
        <Stack
          spacing={4}
          justifyContent="flex-end"
          direction="column"
          sx={{ width: 1 }}
        >
          <Stack
            spacing={2}
            justifyContent="flex-end"
            direction={{ xs: "column", md: "row" }}
            sx={{ width: 1 }}
          >
            <Tooltip
              placement="top-end"
              title="You can enter the percentage discount here for the total amount available here. The amount discounted will be automatically calculated and displayed in the Discount Field."
            >
              <TextField
                size="small"
                label="Discount (%)"
                name="discount_percent"
                value={values.discountPercent}
                onChange={(event) => {
                  const percent = Number(event.target.value);
                  if (percent <= 100) {
                    const total = parseFloat(
                      values.products
                        .map(
                          (obj) =>
                            Number(obj.price) * Number(obj.displayedQuantity),
                        )
                        .reduce((partialSum, a) => partialSum + a, 0) || 0,
                    );
                    const discount =
                      Math.round((percent / 100) * Number(total) * 100) / 100;
                    setFieldValue("discount", discount);
                    setFieldValue("discountPercent", percent);
                  } else {
                    enqueueSnackbar(
                      `Discount percentage cannot be greater than 100.`,
                      {
                        variant: "warning",
                      },
                    );
                  }
                }}
                sx={{ maxWidth: { md: 200 } }}
              />
            </Tooltip>
            <TextField
              size="small"
              label="Discount"
              name="discount_flat"
              disabled={Boolean(values.discountPercent)}
              value={values.discount}
              onChange={(event) =>
                setFieldValue("discount", Number(event.target.value))
              }
              sx={{ maxWidth: { md: 200 } }}
            />

            <TextField
              size="small"
              label="VAT"
              type="number"
              name="tax"
              value={(values.tax || "").toString()}
              onChange={(event) =>
                setFieldValue("tax", Number(event.target.value))
              }
              sx={{ maxWidth: { md: 200 } }}
            />
            <TextField
              size="small"
              label="Tip"
              type="number"
              name="tip"
              value={values.tip.toString()}
              onChange={(event) =>
                setFieldValue("tip", Number(event.target.value))
              }
              helperText="*Specify tip amount here."
              sx={{ maxWidth: { md: 200 } }}
            />
          </Stack>
          <Stack
            spacing={2}
            justifyContent="flex-end"
            direction={{ xs: "column", md: "row" }}
            sx={{ width: 1 }}
          >
            <TextField
              disabled
              size="small"
              name="total"
              label="Total Payable"
              value={fNumber(payable)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">&#8358;</InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              name="amount"
              type="number"
              label={values.status === "paid" ? "Amount Paid" : "Amount To Pay"}
              value={values.amount.toString()}
              onChange={(event) => {
                const amount = parseFloat(event.target.value || 0);
                setFieldValue("amount", Number(event.target.value));
                setFieldValue("change", Number(amount - payable));
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">&#8358;</InputAdornment>
                ),
              }}
            />
            <TextField
              size="small"
              disabled
              name="change"
              label="Change"
              value={fNumber(values.change)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">&#8358;</InputAdornment>
                ),
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      <RequestAccess
        open={open}
        handleClose={() => setOpen(false)}
        handleGrant={handleGrant}
        values={loginValues}
        loading={loading}
        setValue={handleValuesChange}
        showPassword={showPassword}
        handleShowPassword={() => setShowPassword(!showPassword)}
      />
      {Boolean(openQty) && (
        <Popper
          sx={{ zIndex: 99 }}
          open={Boolean(openQty)}
          anchorEl={openQty}
          role={undefined}
          transition
          disablePortal
        >
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{
                transformOrigin:
                  placement === "bottom" ? "center top" : "center bottom",
              }}
            >
              <Paper>
                <ClickAwayListener onClickAway={handleQtyClose}>
                  <MenuList id="split-download-menu" autoFocusItem>
                    <MenuItem
                      sx={{
                        fontSize: "0.8125rem",
                        py: { md: 0.5, xs: 1 },
                      }}
                      onClick={() => handleAddQty(0.5, qtySelectedIndex)}
                    >
                      +1/2
                    </MenuItem>
                    <MenuItem
                      sx={{
                        fontSize: "0.8125rem",
                        py: { md: 0.5, xs: 1 },
                      }}
                      onClick={() => handleAddQty(0.25, qtySelectedIndex)}
                    >
                      +1/4
                    </MenuItem>
                    <MenuItem
                      onClick={() => handleAddQty(0.125, qtySelectedIndex)}
                      sx={{
                        fontSize: "0.8125rem",
                        py: { md: 0.5, xs: 1 },
                      }}
                    >
                      +1/8
                    </MenuItem>
                    <MenuItem
                      sx={{
                        fontSize: "0.8125rem",
                        py: { md: 0.5, xs: 1 },
                      }}
                      onClick={() => handleAddQty(0.1, qtySelectedIndex)}
                    >
                      +1/10
                    </MenuItem>
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      )}
    </Box>
  );
}
